// src/components/timeline/TimelineMedia.tsx

"use client";

import { useState, useRef, useEffect } from "react";
import type { CSSProperties } from "react";
import { TimelineMediaFile } from "@/lib/timeline";
import BlurhashPlaceholder from "./BlurhashPlaceholder";
import Lightbox from "./Lightbox";
import { useMediaManager } from "@/hooks/useMediaManager";
import { useVideoFrame } from "@/hooks/useVideoFrame";

type Props = {
  file: TimelineMediaFile;
  compact?: boolean;
};

function isImageCached(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
    if (img.complete) resolve(true);
  });
}

function formatBytes(size?: number | null) {
  if (!size) return null;

  const units = ["B", "KB", "MB", "GB"];
  let value = size;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${
    units[unitIndex]
  }`;
}

function getPreviewUrl(file: TimelineMediaFile) {
  return (
    file.preview_image?.url ||
    file.thumbnail?.url ||
    file.cover?.url ||
    file.poster?.url ||
    file.url ||
    file.download_url
  );
}

function getPosterUrl(file: TimelineMediaFile) {
  return (
    file.poster?.url ||
    file.cover?.url ||
    file.thumbnail?.url ||
    file.preview_image?.url ||
    undefined
  );
}

/** Only real IMAGE covers — never falls back to the video file URL. */
function getCoverUrl(file: TimelineMediaFile): string | undefined {
  return (
    file.poster?.url ||
    file.cover?.url ||
    file.thumbnail?.url ||
    file.preview_image?.url ||
    undefined
  );
}

function getFullUrl(file: TimelineMediaFile) {
  return file.download_url || file.url;
}

function getKindLabel(kind: TimelineMediaFile["media_kind"]) {
  if (kind === "image") return "Image";
  if (kind === "video") return "Video";
  if (kind === "audio") return "Audio";
  return "File";
}

function getKindIcon(kind: TimelineMediaFile["media_kind"]) {
  if (kind === "image") return "IMG";
  if (kind === "video") return "VID";
  if (kind === "audio") return "AUD";
  return "FILE";
}

export default function TimelineMedia({ file, compact = false }: Props) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const { registerMedia } = useMediaManager();

  const fullUrl = getFullUrl(file);

  // ── Browser-side frame extraction for cover-less videos ─────────────
  // Enabled only for videos that have NO real cover, so we don't pay for
  // it twice. Safe to call unconditionally (the hook short-circuits).
  const realCover = file.media_kind === "video" ? getCoverUrl(file) : undefined;
  const generatedVideoCover = useVideoFrame(
    file.media_kind === "video" ? fullUrl : undefined,
    file.media_kind === "video" && !realCover,
  );

  const previewUrl = getPreviewUrl(file);
  const posterUrl = getPosterUrl(file);
  const sizeLabel = formatBytes(file.size);

  useEffect(() => {
    if (
      (file.media_kind === "image" || file.media_kind === "video") &&
      previewUrl
    ) {
      isImageCached(previewUrl).then((cached) => {
        if (cached) setImageLoaded(true);
      });
    }
  }, [previewUrl, file.media_kind]);

  const wrapperStyle: CSSProperties = {
    position: "relative",
    width: "100%",
    height: compact ? "100%" : undefined,
    minHeight: compact ? 180 : undefined,
    overflow: "hidden",
    borderRadius: 14,
    background: "color-mix(in srgb, currentColor 5%, transparent)",
    border: "1px solid color-mix(in srgb, currentColor 8%, transparent)",
  };

  const badgeStyle: CSSProperties = {
    position: "absolute",
    top: 10,
    left: 10,
    zIndex: 3,
    borderRadius: "999px",
    padding: "0.28rem 0.5rem",
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    color: "#fff",
    background: "rgba(0,0,0,0.42)",
    backdropFilter: "blur(10px)",
  };

  const bottomOverlayStyle: CSSProperties = {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 3,
    padding: "2.25rem 0.8rem 0.75rem",
    color: "#fff",
    background:
      "linear-gradient(to top, rgba(0,0,0,0.58), rgba(0,0,0,0.22), transparent)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 10,
    pointerEvents: "none",
  };

  const fileNameStyle: CSSProperties = {
    minWidth: 0,
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.78rem",
    opacity: 0.92,
  };

  const actionButtonStyle: CSSProperties = {
    border: "1px solid rgba(255,255,255,0.22)",
    background: "rgba(0,0,0,0.36)",
    color: "#fff",
    borderRadius: "999px",
    padding: "0.42rem 0.58rem",
    fontSize: "0.75rem",
    textDecoration: "none",
    backdropFilter: "blur(10px)",
    pointerEvents: "auto",
    flexShrink: 0,
  };

  if (!fullUrl) return null;

  // ─────────────────────────────────────────────────────────────
  // IMAGE
  // ─────────────────────────────────────────────────────────────
  if (file.media_kind === "image") {
    if (!previewUrl) return null;

    return (
      <>
        <div style={wrapperStyle}>
          {!imageLoaded && (
            <div style={{ position: "absolute", inset: 0 }}>
              <BlurhashPlaceholder
                hash={file.blurhash}
                width="100%"
                height="100%"
                borderRadius={14}
              />
            </div>
          )}

          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            aria-label={`Open ${file.file_name || "image"}`}
            style={{
              display: "block",
              width: "100%",
              height: compact ? "100%" : undefined,
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: "zoom-in",
            }}
          >
            <img
              src={previewUrl}
              alt={file.file_name || ""}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
              style={{
                width: "100%",
                height: compact ? "100%" : "auto",
                minHeight: compact ? 180 : undefined,
                maxHeight: compact ? undefined : 560,
                objectFit: "cover",
                display: "block",
                opacity: imageLoaded ? 1 : 0,
                transition: "opacity 0.22s ease",
              }}
            />
          </button>

          <span style={badgeStyle}>{getKindIcon(file.media_kind)}</span>

          {!compact && (
            <div style={bottomOverlayStyle}>
              <div style={fileNameStyle} title={file.file_name || ""}>
                {file.file_name || getKindLabel(file.media_kind)}
              </div>
              <a
                href={fullUrl}
                target="_blank"
                rel="noreferrer"
                style={actionButtonStyle}
                aria-label="Open image"
              >
                Open
              </a>
            </div>
          )}
        </div>

        <Lightbox
          open={lightboxOpen}
          file={file}
          onClose={() => setLightboxOpen(false)}
        />
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // VIDEO
  // ─────────────────────────────────────────────────────────────
  if (file.media_kind === "video") {
    const handlePlay = () => {
      if (videoRef.current) registerMedia(videoRef.current);
    };

    // Real cover wins. Otherwise use the browser-extracted frame.
    const coverUrl = realCover || generatedVideoCover || undefined;
    const hasCover = Boolean(coverUrl);

    // ── Still no cover yet (extraction running or failed) ─────
    if (!hasCover) {
      return (
        <>
          <div
            style={{
              ...wrapperStyle,
              minHeight: compact ? 180 : 220,
              aspectRatio: compact ? undefined : "16 / 9",
              background:
                "linear-gradient(135deg, #1f2937 0%, #0f172a 60%, #111827 100%)",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              aria-label={`Play ${file.file_name || "video"}`}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                padding: 0,
                border: "none",
                background: "transparent",
                color: "inherit",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  width: compact ? 56 : 78,
                  height: compact ? 56 : 78,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.14)",
                  border: "1px solid rgba(255,255,255,0.28)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: compact ? 22 : 30,
                  color: "#fff",
                  boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
                  backdropFilter: "blur(10px)",
                }}
              >
                ▶
              </span>
            </button>

            <span style={badgeStyle}>{getKindIcon(file.media_kind)}</span>

            {!compact && (
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  padding: "2rem 0.9rem 0.75rem",
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.6), transparent)",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  gap: 10,
                  pointerEvents: "none",
                }}
              >
                <div style={fileNameStyle} title={file.file_name || "Video"}>
                  {file.file_name || "Video"}
                </div>
                <a
                  href={fullUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={actionButtonStyle}
                  aria-label="Open video"
                >
                  Open
                </a>
              </div>
            )}
          </div>

          <Lightbox
            open={lightboxOpen}
            file={file}
            onClose={() => setLightboxOpen(false)}
            videoRef={videoRef}
            onVideoPlay={handlePlay}
          />
        </>
      );
    }

    // ── Has cover (real OR browser-extracted) ─────────────────
    return (
      <>
        <div style={wrapperStyle}>
          {!imageLoaded && (
            <div style={{ position: "absolute", inset: 0 }}>
              <BlurhashPlaceholder
                hash={file.blurhash}
                width="100%"
                height="100%"
                borderRadius={14}
              />
            </div>
          )}

          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            aria-label={`Play ${file.file_name || "video"}`}
            style={{
              display: "block",
              width: "100%",
              height: compact ? "100%" : undefined,
              padding: 0,
              border: "none",
              background: "#111",
              cursor: "pointer",
            }}
          >
            <img
              src={coverUrl}
              alt={file.file_name || ""}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
              style={{
                width: "100%",
                height: compact ? "100%" : "auto",
                minHeight: compact ? 180 : 220,
                maxHeight: compact ? undefined : 560,
                objectFit: "cover",
                display: "block",
                opacity: imageLoaded ? 1 : 0,
                transition: "opacity 0.22s ease",
              }}
            />

            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  width: compact ? 56 : 74,
                  height: compact ? 56 : 74,
                  borderRadius: "50%",
                  background: "rgba(0,0,0,0.48)",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: compact ? 22 : 30,
                  boxShadow: "0 12px 36px rgba(0,0,0,0.3)",
                  backdropFilter: "blur(10px)",
                }}
              >
                ▶
              </span>
            </span>
          </button>

          <span style={badgeStyle}>{getKindIcon(file.media_kind)}</span>

          {!compact && (
            <div style={bottomOverlayStyle}>
              <div style={fileNameStyle} title={file.file_name || ""}>
                {file.file_name || getKindLabel(file.media_kind)}
              </div>
              <a
                href={fullUrl}
                target="_blank"
                rel="noreferrer"
                style={actionButtonStyle}
                aria-label="Open video"
              >
                Open
              </a>
            </div>
          )}
        </div>

        <Lightbox
          open={lightboxOpen}
          file={file}
          onClose={() => setLightboxOpen(false)}
          videoRef={videoRef}
          onVideoPlay={handlePlay}
        />
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // AUDIO
  // ─────────────────────────────────────────────────────────────
  if (file.media_kind === "audio") {
    const cover =
      file.cover?.url || file.thumbnail?.url || file.preview_image?.url;

    const handlePlay = () => {
      if (audioRef.current) registerMedia(audioRef.current);
    };

    return (
      <div
        style={{
          ...wrapperStyle,
          display: "flex",
          gap: "0.85rem",
          alignItems: "center",
          padding: compact ? "0.85rem" : "1rem",
          minHeight: compact ? 180 : undefined,
          background:
            "linear-gradient(135deg, color-mix(in srgb, currentColor 5%, transparent), color-mix(in srgb, currentColor 2%, transparent))",
        }}
      >
        <div
          style={{
            width: compact ? 58 : 72,
            height: compact ? 58 : 72,
            borderRadius: 14,
            overflow: "hidden",
            flexShrink: 0,
            background: "color-mix(in srgb, currentColor 10%, transparent)",
          }}
        >
          {cover ? (
            <img
              src={cover}
              alt={file.file_name || ""}
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <BlurhashPlaceholder
              hash={file.blurhash}
              width="100%"
              height="100%"
              borderRadius={14}
            />
          )}
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: "0.55rem",
            }}
          >
            <span
              style={{
                borderRadius: "999px",
                padding: "0.2rem 0.45rem",
                fontSize: "0.64rem",
                fontWeight: 800,
                background: "color-mix(in srgb, currentColor 10%, transparent)",
              }}
            >
              AUD
            </span>

            <div
              style={{
                minWidth: 0,
                flex: 1,
                fontSize: "0.9rem",
                fontWeight: 650,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={file.file_name || "Audio"}
            >
              {file.file_name || "Audio"}
            </div>

            {sizeLabel && (
              <span style={{ fontSize: "0.72rem", opacity: 0.5 }}>
                {sizeLabel}
              </span>
            )}
          </div>

          <audio
            ref={audioRef}
            controls
            preload="none"
            onPlay={handlePlay}
            style={{ width: "100%" }}
          >
            <source src={fullUrl} />
          </audio>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // FILE
  // ─────────────────────────────────────────────────────────────
  return (
    <a
      href={fullUrl}
      target="_blank"
      rel="noreferrer"
      style={{
        ...wrapperStyle,
        display: "flex",
        alignItems: "center",
        gap: "0.85rem",
        padding: "1rem",
        textDecoration: "none",
        color: "inherit",
        minHeight: compact ? 180 : undefined,
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.72rem",
          fontWeight: 800,
          background: "color-mix(in srgb, currentColor 10%, transparent)",
        }}
      >
        FILE
      </div>

      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontWeight: 650,
            fontSize: "0.92rem",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={file.file_name || "Download file"}
        >
          {file.file_name || "Download file"}
        </div>

        <div style={{ marginTop: 4, fontSize: "0.76rem", opacity: 0.55 }}>
          {sizeLabel ? `${sizeLabel} · ` : ""}
          Open file
        </div>
      </div>

      <span
        style={{
          border: "1px solid var(--card-border)",
          borderRadius: "999px",
          padding: "0.42rem 0.65rem",
          fontSize: "0.78rem",
          flexShrink: 0,
        }}
      >
        Open
      </span>
    </a>
  );
}
